import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Check, FileText, User, CreditCard, Shield, Mail, Phone, MapPin, Calendar, Lock, MessageSquare } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientIntakePage = () => {
  // ============ STATE PERSISTENCE FOR CLIENT INTAKE ============
  const getInitialFormData = () => {
    try {
      const saved = localStorage.getItem('clientIntake_formData');
      return saved ? JSON.parse(saved) : {
        fullName: '',
        dateOfBirth: '',
        ssn: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        phone: '',
        email: '',
        nameSuffix: '',
        creditKarmaLogin: '',
        experianLogin: '',
        transunionDisputeLogin: '',
        myEquifaxLogin: ''
      };
    } catch {
      return {
        fullName: '',
        dateOfBirth: '',
        ssn: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        phone: '',
        email: '',
        nameSuffix: '',
        creditKarmaLogin: '',
        experianLogin: '',
        transunionDisputeLogin: '',
        myEquifaxLogin: ''
      };
    }
  };
  
  const [formData, setFormData] = useState(getInitialFormData);

  const [idFile, setIdFile] = useState(null);
  const [ssnCardFile, setSsnCardFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const idInputRef = useRef(null);
  const ssnInputRef = useRef(null);
  
  // Save form data to localStorage when it changes
  useEffect(() => {
    if (formData.fullName || formData.email || formData.phone) {
      const timeoutId = setTimeout(() => {
        try {
          localStorage.setItem('clientIntake_formData', JSON.stringify(formData));
        } catch (e) {
          console.warn('Could not save intake form data:', e);
        }
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [formData]);
  
  // Clear saved data after successful submission
  useEffect(() => {
    if (isSubmitted) {
      localStorage.removeItem('clientIntake_formData');
    }
  }, [isSubmitted]);
  
  // Warn before leaving with unsaved data
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (formData.fullName || formData.email) {
        e.preventDefault();
        e.returnValue = 'You have unsaved information. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formData.fullName, formData.email]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = (file, type) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const fileData = {
          name: file.name,
          type: file.type,
          size: file.size,
          data: e.target.result
        };
        if (type === 'id') {
          setIdFile(fileData);
        } else {
          setSsnCardFile(fileData);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/client-intake/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          idFile: idFile?.data || null,
          ssnCardFile: ssnCardFile?.data || null
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsSubmitted(true);
      } else {
        setError(data.detail || 'Failed to submit form. Please try again.');
      }
    } catch (err) {
      console.error('Submission error:', err);
      setError('Failed to submit form. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success screen after submission
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-6">
        <div className="max-w-lg w-full bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 border border-green-500/30 shadow-2xl text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Thank You!</h1>
          <div className="text-gray-300 space-y-4">
            <p className="text-lg">
              Thank you for submitting your information.
            </p>
            <p>
              <strong className="text-yellow-400">Please standby for codes</strong> and after all codes are submitted and documents are uploaded.
            </p>
            <p>
              <strong className="text-green-400">Be looking for an email for next steps.</strong>
            </p>
          </div>
          <div className="mt-8 pt-6 border-t border-gray-700">
            <p className="text-sm text-gray-500">
              Clear All Credit Repair
            </p>
            <p className="text-sm text-gray-400 mt-2">
              Questions? Call us at <strong>(619) 882-0259</strong>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Shield className="w-10 h-10 text-green-400" />
            <h1 className="text-3xl font-bold text-white">Clear All Credit Repair</h1>
          </div>
          <h2 className="text-xl text-gray-300">Client Intake Form</h2>
          <p className="text-gray-500 mt-2">Please complete all fields to begin your credit repair journey</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-300 text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Personal Information Section */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <div className="flex items-center gap-3 mb-6">
              <User className="w-6 h-6 text-blue-400" />
              <h3 className="text-xl font-semibold text-white">Personal Information</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-3">
                  <label className="block text-gray-400 text-sm mb-2">Full Legal Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    placeholder="John Michael Smith"
                    className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Suffix</label>
                  <select
                    name="nameSuffix"
                    value={formData.nameSuffix}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">None</option>
                    <option value="Jr.">Jr.</option>
                    <option value="Sr.">Sr.</option>
                    <option value="II">II</option>
                    <option value="III">III</option>
                    <option value="IV">IV</option>
                    <option value="V">V</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Date of Birth *
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  required
                  className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">
                  <Lock className="w-4 h-4 inline mr-1" />
                  Social Security Number *
                </label>
                <input
                  type="text"
                  name="ssn"
                  value={formData.ssn}
                  onChange={handleInputChange}
                  required
                  placeholder="XXX-XX-XXXX"
                  className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-gray-400 text-sm mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Street Address *
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  placeholder="123 Main Street, Apt 4B"
                  className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">City *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  placeholder="Los Angeles"
                  className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">State *</label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                    className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Select</option>
                    {['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'].map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">ZIP Code *</label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    required
                    placeholder="90001"
                    className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  placeholder="(555) 555-5555"
                  className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="john@email.com"
                  className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Document Upload Section */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-6 h-6 text-purple-400" />
              <h3 className="text-xl font-semibold text-white">Document Upload</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* ID Upload */}
              <div>
                <label className="block text-sm mb-2">
                  <span className="text-red-400 font-bold">MANDATORY:</span>
                  <span className="text-gray-400 ml-2">California ID or Passport</span>
                </label>
                <input
                  type="file"
                  ref={idInputRef}
                  onChange={(e) => handleFileUpload(e.target.files[0], 'id')}
                  accept="image/*,.pdf"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => idInputRef.current?.click()}
                  className={`w-full p-6 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 transition-colors ${
                    idFile 
                      ? 'border-green-500 bg-green-900/20 text-green-400' 
                      : 'border-red-500/50 text-gray-400 hover:border-red-500 hover:bg-gray-800/50'
                  }`}
                >
                  {idFile ? (
                    <>
                      <Check className="w-8 h-8" />
                      <span className="text-sm font-medium">{idFile.name}</span>
                      <span className="text-xs text-gray-500">Click to change</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8" />
                      <span className="text-sm">Upload California ID or Passport</span>
                      <span className="text-xs text-gray-500">JPG, PNG, or PDF</span>
                    </>
                  )}
                </button>
              </div>

              {/* SSN Card Upload */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Social Security Card (if you have it)</label>
                <input
                  type="file"
                  ref={ssnInputRef}
                  onChange={(e) => handleFileUpload(e.target.files[0], 'ssn')}
                  accept="image/*,.pdf"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => ssnInputRef.current?.click()}
                  className={`w-full p-6 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 transition-colors ${
                    ssnCardFile 
                      ? 'border-green-500 bg-green-900/20 text-green-400' 
                      : 'border-gray-600 text-gray-400 hover:border-gray-500 hover:bg-gray-800/50'
                  }`}
                >
                  {ssnCardFile ? (
                    <>
                      <Check className="w-8 h-8" />
                      <span className="text-sm font-medium">{ssnCardFile.name}</span>
                      <span className="text-xs text-gray-500">Click to change</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8" />
                      <span className="text-sm">Upload Social Security Card</span>
                      <span className="text-xs text-gray-500">JPG, PNG, or PDF</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Credit Monitoring Logins Section */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="w-6 h-6 text-yellow-400" />
              <h3 className="text-xl font-semibold text-white">Credit Monitoring Logins</h3>
            </div>
            <p className="text-gray-500 text-sm mb-6">
              Please provide your login information for each credit monitoring service. This helps us access your reports to dispute inaccurate items.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-400 text-sm mb-2">
                  <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Credit Karma Login
                </label>
                <textarea
                  name="creditKarmaLogin"
                  value={formData.creditKarmaLogin}
                  onChange={handleInputChange}
                  placeholder="Username: example@email.com&#10;Password: ********"
                  rows={3}
                  className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">
                  <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Experian Login
                </label>
                <textarea
                  name="experianLogin"
                  value={formData.experianLogin}
                  onChange={handleInputChange}
                  placeholder="Username: example@email.com&#10;Password: ********"
                  rows={3}
                  className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">
                  <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                  TransUnion Dispute Login
                </label>
                <textarea
                  name="transunionDisputeLogin"
                  value={formData.transunionDisputeLogin}
                  onChange={handleInputChange}
                  placeholder="Username: example@email.com&#10;Password: ********"
                  rows={3}
                  className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">
                  <span className="inline-block w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                  MyEquifax.com Login
                </label>
                <textarea
                  name="myEquifaxLogin"
                  value={formData.myEquifaxLogin}
                  onChange={handleInputChange}
                  placeholder="Username: example@email.com&#10;Password: ********"
                  rows={3}
                  className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="text-center space-y-3">
            {/* Feb 2026 — full field-level gate.  Every required field must
                be filled AND the mandatory California ID/Passport must be
                uploaded before Submit unlocks.  Prevents half-empty
                intake emails from ever leaving the client's browser. */}
            {(() => {
              const missing = [];
              if (!formData.fullName.trim()) missing.push('Full Name');
              if (!formData.dateOfBirth) missing.push('Date of Birth');
              if (!formData.ssn.trim()) missing.push('SSN');
              if (!formData.address.trim()) missing.push('Address');
              if (!formData.city.trim()) missing.push('City');
              if (!formData.state) missing.push('State');
              if (!formData.zipCode.trim()) missing.push('ZIP Code');
              if (!formData.phone.trim()) missing.push('Phone');
              if (!formData.email.trim()) missing.push('Email');
              if (!idFile) missing.push('California ID / Passport');
              const canSubmit = missing.length === 0 && !isSubmitting;
              return (
                <>
                  {missing.length > 0 && (
                    <p className="text-yellow-400 text-sm bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-2 inline-block" data-testid="intake-missing-list">
                      Please fill in: <span className="font-semibold">{missing.join(', ')}</span>
                    </p>
                  )}
                  <div className="flex flex-col sm:flex-row justify-center items-center gap-3">
                    <button
                      type="submit"
                      disabled={!canSubmit}
                      data-testid="intake-submit-btn"
                      className="px-12 py-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-bold text-lg rounded-lg transition-all shadow-lg hover:shadow-green-500/25"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Emailing your info…
                        </span>
                      ) : (
                        <span className="flex items-center gap-2 justify-center">
                          <Mail className="w-5 h-5" /> Email My Information
                        </span>
                      )}
                    </button>
                    {/* Fallback — some clients prefer to text the info instead
                        of emailing.  Opens the phone's native SMS app with
                        a pre-filled body containing every field they typed.
                        Great when their email is broken or they haven't
                        uploaded documents through this form yet.  */}
                    <a
                      href={(() => {
                        const lines = [
                          'Clear All Credit Repair — Client Intake',
                          '',
                          `Name: ${formData.fullName}${formData.nameSuffix ? ' ' + formData.nameSuffix : ''}`,
                          `DOB: ${formData.dateOfBirth}`,
                          `SSN: ${formData.ssn}`,
                          `Address: ${formData.address}, ${formData.city}, ${formData.state} ${formData.zipCode}`,
                          `Phone: ${formData.phone}`,
                          `Email: ${formData.email}`,
                          '',
                          `Credit Karma: ${formData.creditKarmaLogin || '(not provided)'}`,
                          `Experian: ${formData.experianLogin || '(not provided)'}`,
                          `TransUnion: ${formData.transunionDisputeLogin || '(not provided)'}`,
                          `MyEquifax: ${formData.myEquifaxLogin || '(not provided)'}`,
                        ].join('\n');
                        return `sms:+16198820259?&body=${encodeURIComponent(lines)}`;
                      })()}
                      className={`inline-flex items-center gap-2 justify-center px-8 py-4 rounded-lg font-bold text-lg transition-all ${
                        missing.length > 3
                          ? 'bg-slate-700 text-slate-400 cursor-not-allowed pointer-events-none'
                          : 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg hover:shadow-blue-500/25'
                      }`}
                      data-testid="intake-text-btn"
                      title="Open your phone's SMS app pre-filled with all your info — send it as a text instead of email"
                    >
                      <MessageSquare className="w-5 h-5" /> Text It To Me Instead
                    </a>
                  </div>
                </>
              );
            })()}
            <p className="text-gray-500 text-xs mt-4">
              Your information is encrypted and secure.  Submitting will email everything (plus your ID upload) straight to Clear All Credit Repair.
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="text-center mt-8 pt-6 border-t border-gray-800">
          <p className="text-gray-400 text-sm font-medium">
            Clear All Credit Repair
          </p>
          <p className="text-gray-500 text-sm mt-2">
            Questions? Call us at <span className="text-green-400 font-semibold">(619) 882-0259</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClientIntakePage;
